import { alpha, Box, Typography, Mui, MuiIcons } from '@leap4/frontend';
import dayjs from 'dayjs';
import { ReactNode, useContext, useMemo, useState } from 'react';
import { ReportContext } from '../reportContext';

const { ToggleButton, ToggleButtonGroup, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Collapse } = Mui;
const {
    CalendarMonth, Insights, ViewColumn, Campaign: CampaignIcon, StackedLineChart, FilterAlt, Functions, Speed,
    Groups, AutoGraph, Download, BookmarkBorder, PieChartOutlined, ExpandMore, ExpandLess,
} = MuiIcons;

type ReportMode = 'pixel' | 'advertiser';

interface IColumn {
    key: string;
    label: string;
    text: string;
    color: string;
    decimals: number;
}

const SERIES = ['#6366F1', '#059669', '#F59E0B', '#EC4899'];
const EXAMPLE_EVENTS = 3;

// Example rate per served impression by kind of event, so the fake numbers keep believable proportions.
const exampleRate = (eventKey: string): number => {
    if (/visit|view|page|land/.test(eventKey)) return 0.0172;
    if (/verified/.test(eventKey)) return 0.00004;
    if (/start|begin|register|signup|lead/.test(eventKey)) return 0.0009;
    return 0.00016;
};

const card = { border: 1, borderColor: 'divider', borderRadius: '12px', bgcolor: 'background.paper', p: 2, minWidth: 0 } as const;

/** Stable per campaign, so the example doesn't change between visits. */
const seeded = (seed: string) => {
    let h = 2166136261;
    for (let i = 0; i < seed.length; i += 1) h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
    return () => {
        h = Math.imul(h ^ (h >>> 15), 2246822507) ^ Math.imul(h ^ (h >>> 13), 3266489909);
        return ((h >>>= 0) % 10000) / 10000;
    };
};

const fmt = (value: number, decimals: number) => value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const listOf = (items: string[]) => (items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);

const IconTile = ({ children }: { children: ReactNode }) => (
    <Box aria-hidden="true" sx={{ width: 32, height: 32, borderRadius: '8px', display: 'grid', placeItems: 'center', bgcolor: 'action.hover', color: 'primary.main', flexShrink: 0, '& svg': { fontSize: 18 } }}>
        {children}
    </Box>
);

const Fact = ({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) => (
    <Box sx={{ ...card, display: 'flex', gap: 1.5 }}>
        <IconTile>{icon}</IconTile>
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.8125rem', mb: 0.25 }}>{title}</Typography>
            <Typography variant="body2" color="text.secondary">
                {children}
            </Typography>
        </Box>
    </Box>
);

/** Written for the campaigns in scope: their names, their events, and only the features they actually have. */
const ReportingGuide = () => {
    const { report, campaignOptions, campaignEventOptions, campaignCalculatedMetrics, metadata } = useContext(ReportContext);
    const [mode, setMode] = useState<ReportMode>(report.reportType === 'advertiser' ? 'advertiser' : 'pixel');
    const [active, setActive] = useState<string | null>(null);
    const [frequencyOpen, setFrequencyOpen] = useState(false);

    // A one-campaign account never sees talk of picking, splitting or combining campaigns.
    const single = (campaignOptions?.length ?? 0) <= 1;
    const inScope = report.idCampaign === 'all' ? campaignOptions : report.idCampaign.split(',').filter(Boolean);
    const multiScope = inScope.length > 1;
    const campaignName = multiScope ? 'the selected campaigns' : inScope[0] || 'your campaign';
    const exampleCampaigns = multiScope ? inScope.slice(0, 2) : [inScope[0] || 'your campaign'];

    const events = mode === 'pixel' ? campaignEventOptions.filter((e) => e.isAttributed) : campaignEventOptions;
    const shownEvents = events.slice(0, EXAMPLE_EVENTS);
    const eventKey = shownEvents.map((e) => e.key).join('|');
    const eventNames = listOf(shownEvents.map((e) => e.value));
    const predicted = campaignEventOptions.filter((e) => e.isPredicted).map((e) => `Predicted ${e.value}`);
    const calculated = campaignCalculatedMetrics.filter((c) => c.target_mode === 'new').map((c) => c.display_label);
    const hasReach = metadata.metrics.some((m) => m.key === 'reach' && m.selectable !== false);

    const columns = useMemo<IColumn[]>(() => {
        const main: IColumn =
            mode === 'pixel'
                ? { key: 'main', label: 'Served Impressions', text: "Impressions served on that row's impression date.", color: SERIES[0], decimals: 0 }
                : { key: 'main', label: 'Advertiser Events', text: 'Every event received that day, all event types combined.', color: SERIES[0], decimals: 0 };
        const eventCols = shownEvents.map<IColumn>((e, i) =>
            mode === 'pixel'
                ? {
                      key: e.key,
                      label: `${e.value} (Attributed)`,
                      text: i === 0 ? `${e.value} credit earned by that day's impressions. Credit is fractional, so values can have decimals.` : `${e.value} credit for the same impressions.`,
                      color: SERIES[i + 1],
                      decimals: 2,
                  }
                : { key: e.key, label: e.value, text: `Raw count of ${e.value} events received that day.`, color: SERIES[i + 1], decimals: 0 },
        );
        return [main, ...eventCols];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, eventKey]);

    const scopeKey = inScope.join(',');
    const example = useMemo(() => {
        const rand = seeded(`${scopeKey}|${mode}`);
        const dates = [2, 1].map((d) => dayjs().subtract(d, 'day').format('YYYY-MM-DD'));
        const rows = exampleCampaigns.flatMap((campaign) =>
            dates.map((date) => {
                const base = 150000 + Math.round(rand() * 120000);
                const values = columns.map((col, i) => {
                    if (i === 0) return mode === 'pixel' ? base : Math.round(base * 0.019 * (0.9 + rand() * 0.2));
                    const raw = base * exampleRate(col.key) * (0.85 + rand() * 0.3);
                    return col.decimals ? raw : Math.round(raw);
                });
                return { date, campaign, values };
            }),
        );
        const totals = columns.map((_, i) => rows.reduce((sum, r) => sum + r.values[i], 0));
        return { rows, totals };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns, scopeKey, mode]);

    const ownership = campaignName === 'your campaign' ? 'your' : multiScope ? "the selected campaigns'" : `${campaignName}'s`;
    const glance =
        mode === 'pixel'
            ? [
                  { icon: <CalendarMonth />, title: 'Date axis', text: 'Impression date: the day the ad was served. The time range filters on it.' },
                  { icon: <Insights />, title: 'Main metric', text: 'Served Impressions, straight from the impression pixels.' },
                  {
                      icon: <ViewColumn />,
                      title: 'Event columns',
                      text: shownEvents.length
                          ? `Each of ${ownership} events (${eventNames}) adds an "(Attributed)" column: the credit those impressions earned for it.`
                          : 'Each attributed event set up for the campaign adds an "(Attributed)" column: the credit the impressions earned for it.',
                  },
              ]
            : [
                  { icon: <CalendarMonth />, title: 'Date axis', text: 'Conversion date: the day the event happened. The time range filters on it.' },
                  { icon: <Insights />, title: 'Main metric', text: 'Advertiser Events: the raw count of every event received.' },
                  {
                      icon: <ViewColumn />,
                      title: 'Event columns',
                      text: shownEvents.length
                          ? `${eventNames} each show as a raw count. Nothing in this report type is attributed or fractional.`
                          : 'Each event shows as its own raw count. Nothing in this report type is attributed or fractional.',
                  },
              ];

    return (
        <Box sx={{ maxWidth: 1100 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
                <Box>
                    <Typography variant="h5" component="h2">
                        How reporting works{multiScope ? '' : ` for ${campaignName}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Every report is one of two types. The type sets the date axis and which metrics you can pick.
                    </Typography>
                </Box>
                <ToggleButtonGroup exclusive size="small" value={mode} onChange={(_e: unknown, v: ReportMode | null) => v && setMode(v)} aria-label="Report type to explain">
                    <ToggleButton value="pixel" sx={{ px: 2 }}>Impression date</ToggleButton>
                    <ToggleButton value="advertiser" sx={{ px: 2 }}>Conversion date</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{mode === 'pixel' ? 'Impression date report' : 'Conversion date report'}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {mode === 'pixel'
                    ? 'Performance by the day the ads were served. Use it for delivery and attribution.'
                    : 'Activity by the day each conversion happened, as reported to us.'}
            </Typography>
            <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, mb: 3 }}>
                {glance.map((g) => (
                    <Fact key={g.title} icon={g.icon} title={g.title}>{g.text}</Fact>
                ))}
            </Box>

            <Box sx={{ ...card, p: 0, mb: 3, overflow: 'hidden' }}>
                <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>What your results will look like</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Example numbers, not your data. Hover a column or a note to see what it means.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', px: 2, pb: 1.5 }} aria-label="Report totals example">
                    {columns.map((c, i) => (
                        <Box key={c.key} sx={{ border: 1, borderColor: 'divider', borderRadius: '8px', px: 1.25, py: 0.5 }}>
                            <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>{c.label}</Typography>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.8125rem', fontVariantNumeric: 'tabular-nums' }}>{fmt(example.totals[i], c.decimals)}</Typography>
                        </Box>
                    ))}
                </Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{mode === 'pixel' ? 'Impression date' : 'Conversion date'}</TableCell>
                                {multiScope && <TableCell>Campaign</TableCell>}
                                {columns.map((c) => (
                                    <TableCell key={c.key} align="right">{c.label}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {example.rows.map((row, ri) => (
                                <TableRow key={ri}>
                                    <TableCell>{row.date}</TableCell>
                                    {multiScope && <TableCell>{row.campaign}</TableCell>}
                                    {columns.map((c, ci) => (
                                        <TableCell
                                            key={c.key}
                                            align="right"
                                            onMouseEnter={() => setActive(c.key)}
                                            onMouseLeave={() => setActive(null)}
                                            sx={{ fontVariantNumeric: 'tabular-nums', transition: 'background-color 0.12s', bgcolor: active === c.key ? alpha(c.color, 0.16) : undefined }}
                                        >
                                            {fmt(row.values[ci], c.decimals)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 2, display: 'grid', gap: 0.75 }}>
                    {columns.map((c) => (
                        <Box
                            component="li"
                            key={c.key}
                            tabIndex={0}
                            onMouseEnter={() => setActive(c.key)}
                            onMouseLeave={() => setActive(null)}
                            onFocus={() => setActive(c.key)}
                            onBlur={() => setActive(null)}
                            sx={{ display: 'flex', gap: 1, alignItems: 'baseline', borderRadius: '6px', px: 1, py: 0.5, bgcolor: active === c.key ? alpha(c.color, 0.12) : 'transparent', outline: 'none' }}
                        >
                            <Box aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c.color, flexShrink: 0, transform: 'translateY(-1px)' }} />
                            <Typography variant="body2"><strong>{c.label}</strong> <Box component="span" sx={{ color: 'text.secondary' }}>{c.text}</Box></Typography>
                        </Box>
                    ))}
                    <Box component="li" sx={{ display: 'flex', gap: 1, alignItems: 'baseline', px: 1, py: 0.5 }}>
                        <Box aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: 'text.secondary', flexShrink: 0 }} />
                        <Typography variant="body2"><strong>Totals</strong> <Box component="span" sx={{ color: 'text.secondary' }}>The strip above the table covers every page. Ratios and reach are never summed. Also the last line of the CSV.</Box></Typography>
                    </Box>
                </Box>
            </Box>

            <Typography sx={{ fontWeight: 600, mb: 1.25 }}>Good to know</Typography>
            <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, mb: 3 }}>
                {!single && (
                    <Fact icon={<CampaignIcon />} title="Campaigns">
                        Rows add up across campaigns unless "Split rows by campaign" is on.
                    </Fact>
                )}
                <Fact icon={<StackedLineChart />} title="Day, Month or Sum">
                    Day and Month group by date first. Sum has no date breakdown.
                </Fact>
                <Fact icon={<FilterAlt />} title="Filters vs. constraints">
                    Filters apply before grouping, constraints after. Totals count only the rows shown.
                </Fact>
                <Fact icon={<Speed />} title="Speed estimate">
                    {mode === 'pixel'
                        ? 'Dimensions marked "fast" keep reports quick. Longer ranges take longer.'
                        : 'Longer ranges take longer.'}
                </Fact>
                {mode === 'pixel' && hasReach && (
                    <Fact icon={<Groups />} title="Reach and Frequency">
                        Reach counts unique IP + device combinations, not people. Frequency is impressions per reach. Both are capped at 31 days and are slower.
                    </Fact>
                )}
                {mode === 'pixel' && predicted.length > 0 && (
                    <Fact icon={<AutoGraph />} title="Predicted metrics">
                        {listOf(predicted)} {predicted.length === 1 ? 'is' : 'are'} the expected final total once the attribution window closes. Impression-date reports only{single ? '' : ', one campaign at a time'}.
                    </Fact>
                )}
                {calculated.length > 0 && (
                    <Fact icon={<Functions />} title="Calculated metrics">
                        {listOf(calculated)}: formulas set up for {campaignName}, recomputed over the whole report for totals.{single ? '' : ' Available when one campaign is selected.'}
                    </Fact>
                )}
                {mode === 'pixel' && (
                    <Fact icon={<PieChartOutlined />} title="Fractional credit">
                        Impressions sharing a conversion split its credit, so attributed columns can show decimals.
                    </Fact>
                )}
                <Fact icon={<Download />} title="Exports">
                    CSV holds every row of the report, not just the page on screen, plus the totals line. Lead IDs lists every lead ID whose conversion falls in the date range{single ? '' : ' for the selected campaigns'}; dimensions, filters and constraints don't apply to it.
                </Fact>
                <Fact icon={<BookmarkBorder />} title="Templates">
                    Shared with your organization. A template saves {single ? '' : 'campaigns, '}dates, dimensions, metrics, filters and constraints{single ? '' : ', for one campaign or for all of them'}. Results, sorting and search aren't saved.
                </Fact>
            </Box>

            {mode === 'pixel' && hasReach && (
                <Box sx={card}>
                    <Button onClick={() => setFrequencyOpen(!frequencyOpen)} endIcon={frequencyOpen ? <ExpandLess /> : <ExpandMore />} aria-expanded={frequencyOpen} sx={{ px: 0.5 }}>
                        Why Frequency looks low in broken-out reports
                    </Button>
                    <Collapse in={frequencyOpen}>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 820 }}>
                            Frequency is scoped to each row and to the date range, and the effect is large. Over a month at campaign level it can sit around 2, while the same month broken out by day and app reads close to 1. Households rarely see one app twice in a day, so a monthly figure builds up across days and apps rather than within them. A broken-out report showing a column of ~1s is correct. Compare against a 4–8 benchmark only at campaign level, over a full month.
                        </Typography>
                    </Collapse>
                </Box>
            )}
        </Box>
    );
};

export default ReportingGuide;
